/** API client for Terra Search Gateway. */

import { getConfig } from './config';

// --- Types matching Search Gateway response schemas ---

export interface ProductResult {
  product_id: number;
  part_number: string;
  name: string;
  url_key: string;
  product_url: string;
  image_url: string;
  additional_images: string[];
  price: number | null;
  msrp: number | null;
  category: string;
  category_url: string;
  product_line: string;
  parent_product_line: string;
  parent_product_id: number | null;
  parent_product_name: string;
  parent_product_url: string;
  sibling_count: number | null;
  description: string;
  display_bullets?: string[];
  stocked: boolean;
  vendor: string;
  display_site_tui?: boolean | null;
  display_site_le?: boolean | null;
  manufacturer?: string;
  dimensions: string;
  weight_lbs: number | null;
  lead_time_days: number | null;
  lead_time_text?: string;
  availability_status?: string;
  availability_message?: string;
  is_parent_product?: boolean;
  price_min?: number | null;
  price_max?: number | null;
  variants_count?: number | null;
  certifications: string;
  iso_classes?: string[];
  warranty: string;
  electrical_specs?: string;
  country_of_origin?: string;
  product_benefit?: string;
  score: number;
  match_type: 'keyword' | 'semantic' | 'hybrid' | 'part_number';
}

export interface FacetBucket {
  value: string;
  count: number;
}

export interface FacetData {
  buckets: FacetBucket[];
}

export interface SearchResponse {
  results: ProductResult[];
  total: number;
  page: number;
  per_page: number;
  search_event_id?: string;
  facets: Record<string, FacetData>;
  search_time_ms: number;
  query_intent: string;
  corrections_applied: Array<{ from: string; to: string }>;
  suggested_correction: string | null;
  parsed_dimensions?: number[];
  parsed_materials?: string[];
  parsed_certifications?: string[];
  parsed_electrical?: string[];
  vocabulary_expansions: string[];
  industry_hint: string | null;
  relaxed_queries: Array<{ query: string; result_count: number }>;
  did_you_mean: string | null;
}

export interface AutocompleteResult {
  product_id: number;
  name: string;
  part_number: string;
  category: string;
  url_key: string;
  image_url: string;
  price: number | null;
  product_url: string;
  product_line: string;
  stocked: boolean;
}

export interface CategorySuggestion {
  name: string;
  count: number;
  category_url?: string;
}

export interface AutocompleteResponse {
  suggestions: AutocompleteResult[];
  categories: CategorySuggestion[];
  popular: string[];
  search_time_ms: number;
}

export interface SearchParams {
  q: string;
  page?: number;
  per_page?: number;
  category?: string;
  certifications?: string;
  vendor?: string;
  site?: string;
  manufacturer?: string;
  in_stock?: boolean;
  price_min?: number;
  price_max?: number;
  sort?: string;
  semantic?: boolean;
  dedup_families?: boolean;
}

// --- Client-side search cache (5-minute TTL) ---

const CACHE_TTL_MS = 5 * 60 * 1000;
const _searchCache = new Map<string, { data: SearchResponse; ts: number }>();

function cacheKey(params: SearchParams): string {
  return JSON.stringify(params);
}

function getCached(params: SearchParams): SearchResponse | null {
  const key = cacheKey(params);
  const entry = _searchCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > CACHE_TTL_MS) {
    _searchCache.delete(key);
    return null;
  }
  return entry.data;
}

function setCache(params: SearchParams, data: SearchResponse): void {
  // Cap cache size at 50 entries to limit memory
  if (_searchCache.size >= 50) {
    const oldest = _searchCache.keys().next().value;
    if (oldest !== undefined) _searchCache.delete(oldest);
  }
  _searchCache.set(cacheKey(params), { data, ts: Date.now() });
}

// --- API functions ---

function authHeaders(): Record<string, string> {
  const { apiKey } = getConfig();
  return apiKey ? { 'X-API-Key': apiKey } : {};
}

export async function search(
  params: SearchParams,
  signal?: AbortSignal,
): Promise<SearchResponse> {
  // Check cache first (skip cache for aborted requests)
  const cached = getCached(params);
  if (cached) return cached;

  const { apiUrl, semantic } = getConfig();
  const url = new URL(`${apiUrl}/search`, window.location.origin);

  url.searchParams.set('q', params.q);
  if (params.page) url.searchParams.set('page', String(params.page));
  if (params.per_page) url.searchParams.set('per_page', String(params.per_page));
  if (params.category) url.searchParams.set('category', params.category);
  if (params.certifications) url.searchParams.set('certifications', params.certifications);
  if (params.vendor) url.searchParams.set('vendor', params.vendor);
  if (params.site) url.searchParams.set('site', params.site);
  if (params.manufacturer) url.searchParams.set('manufacturer', params.manufacturer);
  if (params.in_stock) url.searchParams.set('in_stock', 'true');
  if (params.price_min != null) url.searchParams.set('price_min', String(params.price_min));
  if (params.price_max != null) url.searchParams.set('price_max', String(params.price_max));
  if (params.sort && params.sort !== 'relevance') url.searchParams.set('sort', params.sort);
  url.searchParams.set('semantic', String(params.semantic ?? semantic));
  url.searchParams.set('dedup_families', String(params.dedup_families ?? false));
  // Cloudflare in front of staging.terrauniversal.com caches /api/terra-search
  // responses with `public, max-age=60, stale-while-revalidate=300`. That mixes
  // site-filtered results across tabs (Terra/LE) for up to ~6 minutes after
  // any deploy. Until the gateway sends `Cache-Control: private, no-cache`
  // for personalized responses, append a 30s-bucketed cache-buster so site
  // toggles and post-deploy retests miss the edge cache predictably.
  url.searchParams.set('_cb', String(Math.floor(Date.now() / 30000)));

  const res = await fetch(url.toString(), {
    signal,
    headers: authHeaders(),
    // Bypass any browser HTTP cache that would honor the CDN hint.
    cache: 'no-store',
  });
  if (!res.ok) throw new Error(`Search failed: ${res.status}`);
  const data: SearchResponse = await res.json();
  setCache(params, data);
  return data;
}

export async function autocomplete(
  query: string,
  limit?: number,
  signal?: AbortSignal,
): Promise<AutocompleteResponse> {
  const { apiUrl, autocompleteLimit } = getConfig();
  const url = new URL(`${apiUrl}/autocomplete`, window.location.origin);
  url.searchParams.set('q', query);
  url.searchParams.set('limit', String(limit ?? autocompleteLimit));

  const res = await fetch(url.toString(), { signal, headers: authHeaders() });
  if (!res.ok) throw new Error(`Autocomplete failed: ${res.status}`);
  return res.json();
}

export async function fetchPopular(signal?: AbortSignal): Promise<string[]> {
  const { apiUrl } = getConfig();
  try {
    const res = await fetch(`${apiUrl}/popular`, { signal, headers: authHeaders() });
    if (!res.ok) return [];
    const data = await res.json();
    return data.queries ?? [];
  } catch {
    return [];
  }
}

export interface ClickMetadata {
  search_event_id?: string;
  part_number?: string;
  product_name?: string;
  vendor?: string;
  surface?: string;
  result_type?: string;
  product_url?: string;
  price?: number | null;
}

export function trackClick(query: string, productId: number, position: number, metadata: ClickMetadata = {}): void {
  const { apiUrl, apiKey } = getConfig();
  // Fire-and-forget analytics — always use fetch so we can send X-API-Key
  try {
    const body = JSON.stringify({ query, product_id: productId, position, ...metadata });
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (apiKey) headers['X-API-Key'] = apiKey;
    fetch(`${apiUrl}/analytics/click`, {
      method: 'POST',
      headers,
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Silently ignore analytics failures
  }
}

// --- Family (product siblings) ---

export interface FamilySelectorColumn {
  key: string;
  label: string;
}

export interface FamilyChild {
  product_id: number;
  name: string;
  part_number: string;
  image_url: string;
  price: number | null;
  product_url: string;
  stocked: boolean;
  availability_status?: string;
  lead_time_text?: string;
  dimensions?: string;
  category?: string;
  product_line?: string;
  electrical_specs?: string;
  certifications?: string;
  iso_classes?: string[];
  warranty?: string;
  country_of_origin?: string;
  product_benefit?: string;
  weight_lbs?: number | null;
  selector_values?: Record<string, string>;
}

export interface FamilyResponse {
  parent_id: number;
  parent_name: string;
  parent_url: string;
  selector_columns?: FamilySelectorColumn[];
  children: FamilyChild[];
  total_children: number;
}

export async function fetchFamily(
  parentId: number,
  signal?: AbortSignal,
): Promise<FamilyResponse> {
  const { apiUrl } = getConfig();
  const res = await fetch(`${apiUrl}/family/${parentId}`, {
    signal,
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`Family fetch failed: ${res.status}`);
  return res.json();
}

// --- Helpers ---

export function formatPrice(price: number | null): string {
  if (price == null || price === 0) return '';
  // Terra catalog policy: prices display as whole dollars, rounded UP. A4's
  // TUI_Sell column is integer-valued for all 13,850 active web products,
  // so Math.ceil is a no-op for current data — it's future-proofing in case
  // a record ever ends up with cents (e.g. an inherited $99.50 MSRP-style
  // value). maximumFractionDigits:0 strips the cosmetic ".00" Intl was
  // adding to every integer price.
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Math.ceil(price));
}

export interface DisplayPriceParts {
  amount: string;
  prefix?: string;
  isQuote: boolean;
}

export function getDisplayPriceParts(
  product: Pick<ProductResult, 'is_parent_product' | 'price' | 'price_min' | 'price_max'>,
): DisplayPriceParts | null {
  const isParent = !!product.is_parent_product;
  if (!isParent) {
    const amount = formatPrice(product.price ?? null);
    return amount ? { amount, isQuote: false } : { amount: 'Request Quote', isQuote: true };
  }

  const min = product.price_min;
  const max = product.price_max;
  if (min == null || min === 0) {
    return { amount: 'Request Quote', isQuote: true };
  }

  const amount = formatPrice(min);
  if (!amount) return { amount: 'Request Quote', isQuote: true };
  return {
    amount,
    prefix: max && max > min ? 'From' : undefined,
    isQuote: false,
  };
}

export function getProductUrl(product: { product_url?: string; url_key?: string }): string {
  if (product.product_url) return product.product_url;
  if (product.url_key) return `/${product.url_key}.html`;
  return '';
}

/** Prefetch a product page on hover (inject a <link rel="prefetch"> once per URL).
 *  Browser handles caching and reuses the cached HTML when the user clicks. */
const _prefetched = new Set<string>();
export function prefetchProductPage(url: string): void {
  if (!url || _prefetched.has(url)) return;
  _prefetched.add(url);
  try {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    link.as = 'document';
    document.head.appendChild(link);
  } catch {
    /* prefetch is a nice-to-have — silent no-op on failure */
  }
}
